/**
 * PNG asset templates for the procedural map's landscape
 * decoration (mountains + rocks + trees).
 *
 * Each template is a pre-authored PNG (transparent background)
 * loaded via Vite's ``import.meta.glob`` at build time.  The
 * procedural layer (``proceduralMap.ts``) picks template
 * indices + positions + scales; ``QuestMap.tsx`` instantiates a
 * scaled, optionally flipped ``<image>`` element at each slot.
 *
 * Drop new PNGs into ``./map_assets/`` following the
 * ``mountain_NN.png`` / ``rock_NN.png`` / ``tree_NN.png``
 * naming and they're picked up automatically.  See the README
 * in that folder for authoring conventions.
 */

export interface MapShapeTemplate {
  /** Bundled asset URL — passed to ``<image href={...}>``. */
  imageUrl: string;
  /** Width / height ratio of the source PNG.  Used by the
   * renderer to compute the rendered height from a target width
   * so the asset stays unstretched. */
  aspect: number;
}

// Eager glob → keyed by relative path under data/.  Each entry's
// value is the bundled asset URL.
const _mountainUrls = import.meta.glob<string>(
  "./map_assets/mountain_*.png",
  { eager: true, query: "?url", import: "default" }
);

const _rockUrls = import.meta.glob<string>(
  "./map_assets/rock_*.png",
  { eager: true, query: "?url", import: "default" }
);

const _treeUrls = import.meta.glob<string>(
  "./map_assets/tree_*.png",
  { eager: true, query: "?url", import: "default" }
);

/** Sort by path so the order is stable across machines —
 * different sort orders would change which template each
 * deterministic index maps to. */
function sortedEntries(glob: Record<string, string>): Array<[string, string]> {
  return Object.entries(glob).sort(([a], [b]) => a.localeCompare(b));
}

// Aspect ratios are approximate — the renderer uses them to
// preserve proportions when scaling the PNG to a target width.
// If your source PNGs deviate significantly, update these
// numbers; the assets list is fed by Vite glob so the array
// length tracks file count automatically.
const MOUNTAIN_ASPECT = 1.6;  // wider than tall (mountain range)
const ROCK_ASPECT = 1.35;     // gently wider than tall (rock pile)
const TREE_ASPECT = 0.75;     // taller than wide (pine tree)

export const MOUNTAIN_TEMPLATES: MapShapeTemplate[] = sortedEntries(
  _mountainUrls,
).map(([, url]) => ({ imageUrl: url, aspect: MOUNTAIN_ASPECT }));

export const ROCK_TEMPLATES: MapShapeTemplate[] = sortedEntries(
  _rockUrls,
).map(([, url]) => ({ imageUrl: url, aspect: ROCK_ASPECT }));

export const TREE_TEMPLATES: MapShapeTemplate[] = sortedEntries(
  _treeUrls,
).map(([, url]) => ({ imageUrl: url, aspect: TREE_ASPECT }));
